import React, { useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, Loader2, UploadCloud, X } from 'lucide-react';
import { createFabricTemplate } from '../../api/fabricTemplate.api';
import { uploadImageToS3 } from '../../utils/s3Upload';

function nameFromFile(file) {
  const raw = String(file?.name || 'Fabric template')
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!raw) return 'Fabric template';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export default function AddFabricTemplateModal({ open, onClose, onCreated, showNotification }) {
  const [designFile, setDesignFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const previewUrl = useMemo(() => {
    if (!designFile) return '';
    return URL.createObjectURL(designFile);
  }, [designFile]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!open) return null;

  const reset = () => {
    setDesignFile(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose?.();
  };

  const handleFileChange = (file) => {
    if (!file) {
      setDesignFile(null);
      return;
    }
    if (!file.type?.startsWith('image/')) {
      showNotification?.('Please choose an image file', 'error');
      return;
    }
    setDesignFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!designFile) {
      showNotification?.('Upload a template image', 'error');
      return;
    }

    setSaving(true);
    try {
      const result = await uploadImageToS3(designFile, {
        folder: 'fabric-templates',
        fileName: `fabric-template-${Date.now()}-${designFile.name}`.replace(/[^a-zA-Z0-9._-]/g, '_'),
      });
      if (!result?.success || !result.url) {
        throw new Error(result?.error || 'Image upload failed');
      }

      const name = nameFromFile(designFile);
      const created = await createFabricTemplate({
        name,
        category: 'Custom',
        tagline: '',
        tags: [],
        productType: name,
        accent: '#1a2744',
        templateKind: 'image',
        designImageUrl: result.url,
        thumbnailUrl: result.url,
      });

      showNotification?.('Fabric template added', 'success');
      reset();
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      console.error(err);
      showNotification?.(err?.response?.data?.message || err?.message || 'Failed to add template', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={handleClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Add fabric template"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Add fabric template</h3>
          <button type="button" onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100" disabled={saving}>
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Template image *</label>
            {previewUrl ? (
              <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-slate-50">
                <img
                  src={previewUrl}
                  alt="Template preview"
                  className="w-full max-h-72 object-contain bg-white"
                />
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 bg-white">
                  <p className="text-xs text-gray-600 truncate min-w-0">{designFile?.name}</p>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setDesignFile(null)}
                    className="text-xs font-medium text-red-600 hover:text-red-700 shrink-0"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-300 rounded-xl p-10 cursor-pointer hover:border-indigo-400 bg-slate-50 transition">
                <div className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <UploadCloud size={24} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-800">Click to upload template image</p>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={saving}
                  onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                />
              </label>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !designFile}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:bg-slate-400 inline-flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
              {saving ? 'Uploading…' : 'Add template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
